import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { RootState, AppDispatch, GetState } from '../store'

import {
  postTaskType,
  deleteTaskType,
  updateTaskType,
  getTaskTypes
} from '../lib/api'
import { sortByFlowOrder } from '@SonarCX/pages/Settings/TaskSettings/DraggableSetting/helpers'
import { AddErrorAlert, AddSuccessAlert } from './alert'

type TaskTypeState = {
  types: Record<string, TaskType>
  typesValues: TaskType[]
  loaded: boolean
  loading: boolean
  error: any
}

const initialState: TaskTypeState = {
  types: {},
  typesValues: [],
  loaded: false,
  loading: false,
  error: null
}

export const taskTypeSlice = createSlice({
  name: 'taskType',
  initialState,
  reducers: {
    loading: (state: TaskTypeState) => {
      state.loading = true
    },

    load: (
      state: TaskTypeState,
      action: PayloadAction<Record<string, TaskType>>
    ) => {
      state.types = action.payload
      state.typesValues = sortByFlowOrder(Object.values(action.payload))
      state.loaded = true
      state.loading = false
    },

    update: (state: TaskTypeState, action: PayloadAction<TaskType>) => {
      const { id } = action.payload
      const copy = { ...state.types, [id]: action.payload }
      state.types = copy
      state.typesValues = sortByFlowOrder(Object.values(copy))
    },

    remove: (state: TaskTypeState, action: PayloadAction<string>) => {
      const copy = { ...state.types }
      delete copy[action.payload]
      state.types = copy
      state.typesValues = sortByFlowOrder(Object.values(copy))
    },

    failed: (state: TaskTypeState, action: PayloadAction<any>) => {
      state.error = action.payload
      state.loaded = true
      state.loading = false
    },

    patch: (
      state: TaskTypeState,
      action: PayloadAction<Record<string, Partial<TaskType>>>
    ) => {
      const data = action.payload

      for (const entry of Object.entries(data)) {
        state.types[entry[0]] = { ...state.types[entry[0]], ...entry[1] }
      }

      const updated = state.typesValues.map(status => {
        return {
          ...status,
          ...(data[status.id] ?? {})
        }
      })

      state.typesValues = sortByFlowOrder(updated)
    }
  }
})

const { loading, load, update, remove, failed, patch } = taskTypeSlice.actions

export const fetchTypes = () => {
  return async (dispatch: AppDispatch) => {
    dispatch(loading())
    try {
      const types = await getTaskTypes()
      const filtered = types.filter(obj => Boolean(obj.name))

      const toSet: Record<string, TaskType> = {}
      filtered.forEach(type => {
        toSet[type.id] = type
      })

      dispatch(load(toSet))
    } catch (err) {
      dispatch(failed(err))
    }
  }
}

export const createType = (name: string) => {
  return async (dispatch: AppDispatch) => {
    const created = await postTaskType({ name, flow_order: null })
    dispatch(update(created))
  }
}

export const deleteType = (id: string) => {
  return async (dispatch: AppDispatch) => {
    await deleteTaskType(id)
    dispatch(remove(id))
  }
}

export const updateType = (data: TaskType) => {
  return async (dispatch: AppDispatch) => {
    const { id } = data

    // @ts-expect-error Can exist in the data object
    delete data.dispName
    delete data.system

    const updated = await updateTaskType(id, data)
    dispatch(update(updated))
  }
}

export const patchTypes = (data: (Partial<TaskType> & { id: string })[]) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    const { typesValues } = selectTaskType(getState())
    try {
      const toUpdate: Record<string, TaskType> = {}

      data.forEach(item => {
        const { id, ...props } = item
        const current = typesValues.find(status => status.id === id)

        if (!current) {
          return
        }

        // Check if any of the updates are new
        const hasUpdate = Object.entries(props).some(
          ([key, val]) => current[key] !== val
        )

        if (hasUpdate) {
          toUpdate[id] = { ...current, ...item }
        }
      })

      // Send updates to server
      Object.values(toUpdate).forEach(status => {
        const { id, system, ...priorityToUpdate } = status

        updateTaskType(id, priorityToUpdate)
      })

      dispatch(patch(toUpdate))

      dispatch(AddSuccessAlert('Changes saved.'))
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }
}

export const selectTaskType = (state: RootState) => state.taskType

export default taskTypeSlice.reducer
